/**
 * Minimal structural helpers over the canonical JsonLogic subset (BAL-8506) — no `json-logic-js`
 * runtime dependency (common must stay browser-safe); we only BUILD and READ plain-object AST nodes,
 * never evaluate them. Reverse maps are DERIVED from `WORKFORCE_CONDITION_FIELDS` so they can't drift.
 */

import {
  WORKFORCE_CONDITION_FIELDS,
  WORKFORCE_CONDITION_FIELD_KEYS,
} from '../../consts/workforce-condition-fields';
import type { WorkforceConditionFieldKey } from '../../consts/workforce-condition-fields';

export type JsonLogicNode = Record<string, unknown>;

export const asObject = (value: unknown): JsonLogicNode | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as JsonLogicNode)
    : null;

/** The args array of a single-operator node (`{ op: [...] }`), or null if `value` isn't that shape. */
export const singleOp = (value: unknown, op: string): unknown[] | null => {
  const node = asObject(value);

  if (!node) return null;

  const keys = Object.keys(node);

  if (keys.length !== 1 || keys[0] !== op) return null;

  return Array.isArray(node[op]) ? (node[op] as unknown[]) : null;
};

/** The path string of a `{ var: 'report.x' }` node (json-logic string form), or null. */
export const getVar = (value: unknown): string | null => {
  const node = asObject(value);

  if (node && Object.keys(node).length === 1 && typeof node.var === 'string') return node.var;

  return null;
};

const isString = (value: unknown): value is string => typeof value === 'string';

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString);

/** The risk-type key of a `{ "==": [{ "var": "report.riskType.<t>.alert" }, true] }` node, else null. */
export const riskTypeAlertKey = (node: unknown): string | null => {
  const args = singleOp(node, '==');

  if (!args || args.length !== 2 || args[1] !== true) return null;

  const path = getVar(args[0]);
  const match = path == null ? null : /^report\.riskType\.(.+)\.alert$/.exec(path);

  return match?.[1] ?? null;
};

const fieldKeysByShape = (shape: string): WorkforceConditionFieldKey[] =>
  WORKFORCE_CONDITION_FIELD_KEYS.filter(key => WORKFORCE_CONDITION_FIELDS[key].shape === shape);

const varToFieldMap = (shape: string): Record<string, WorkforceConditionFieldKey> =>
  Object.fromEntries(
    fieldKeysByShape(shape).map(key => [WORKFORCE_CONDITION_FIELDS[key].factVar, key]),
  );

/** `report.mcc` → `mcc`, etc. — reverse lookup for `scalar-in` fields. */
export const SCALAR_FIELD_BY_VAR = varToFieldMap('scalar-in');
/** `report.contentFindings` → `contentFindings` — reverse lookup for `array-some` fields. */
export const ARRAY_SOME_FIELD_BY_VAR = varToFieldMap('array-some');
/** `report.socialMedia.anyMissing` → `socialMedia`, etc. — reverse lookup for `presence-boolean`. */
export const PRESENCE_FIELD_BY_VAR = varToFieldMap('presence-boolean');

/** The single field whose shape is `risk-type-or` (there is exactly one, by construction). */
export const RISK_TYPE_FIELD_KEY = fieldKeysByShape('risk-type-or')[0]!;
